/**
 * Lotes a partir das linhas da aba GERENCIAL (uma linha por ambiente).
 *
 * Regras (decisões do Igor, 22/10):
 * - Toda linha com algo na coluna do lote entra: números, letras ou outros
 *   caracteres. Não há ASM/ASP na GERENCIAL para excluir.
 * - Lote numérico agrupa pelo número. Lote de texto ("-", "MANUAL", "SERV.")
 *   junta ambientes de clientes diferentes, então agrupa por lote + cliente:
 *   cada folha continua com um cliente só.
 * - Lote sem nenhuma data de otimização (S) não aparece: sem ela não há
 *   datas-limite e a folha sairia vazia.
 * - Em aberto = algum ambiente ainda sem CARREGAMENTO (CN).
 */
import { F } from "../gerencialFields";
import { capitalizarNome } from "../leadFormat";
import { CAMPO_ENTREGA } from "./setores";

const LOTE_NUMERICO = /^\d+$/;

function texto(valor) {
  return valor === null || valor === undefined ? "" : String(valor).trim();
}

function numero(valor) {
  const n = typeof valor === "number" ? valor : Number(texto(valor).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function chaveDoCliente(cliente) {
  return cliente.toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ");
}

function chaveDoLote(numeroDoLote, cliente) {
  if (LOTE_NUMERICO.test(numeroDoLote)) return numeroDoLote;
  return `${numeroDoLote}::${chaveDoCliente(cliente)}`;
}

function paraAmbiente(linha) {
  return {
    cliente: capitalizarNome(texto(linha[F.NOME_DO_CLIENTE])),
    nome: capitalizarNome(texto(linha[F.AMBIENTE])),
    projeto: texto(linha[F.NUMERO_DO_PROJETO]),
    prazoDias: linha[F.PRAZO_EM_DIAS] ?? null,
    mdfM2: numero(linha[F.MDF_M2]),
    pecas: numero(linha[F.PECAS_UN]),
    otimizacao: linha[F.OTIMIZACAO_DO_LOTE] || null,
    carregamento: linha[F.CARREGAMENTO] || null,
    linha,
  };
}

function maiorData(datas) {
  return datas.reduce((maior, data) => (!maior || data > maior ? data : maior), null);
}

function montarLote(chave, numeroDoLote, ambientes) {
  const clientes = [...new Set(ambientes.map((a) => a.cliente))];
  const otimizacoes = [...new Set(ambientes.map((a) => a.otimizacao).filter(Boolean))].sort();
  const etiqueta = ambientes.map((a) => texto(a.linha[F.ETIQUETA])).find(Boolean) || "";
  const numerico = LOTE_NUMERICO.test(numeroDoLote);
  return {
    chave,
    numero: numeroDoLote,
    // "508" ou "MANUAL · Maria Silva": o lote de texto só é único com o cliente.
    rotulo: numerico ? numeroDoLote : `${numeroDoLote} · ${clientes[0]}`,
    cliente: clientes[0],
    clientes,
    etiqueta,
    otimizacao: maiorData(otimizacoes),
    otimizacoes,
    emAberto: ambientes.some((a) => !a.carregamento),
    totalM2: ambientes.reduce((soma, a) => soma + a.mdfM2, 0),
    totalPecas: ambientes.reduce((soma, a) => soma + a.pecas, 0),
    ambientes,
  };
}

/**
 * Lotes da planilha, do otimizado mais recente para o mais antigo. Os
 * ambientes ficam na ordem das linhas da planilha, como nas abas
 * APONTAMENTO.
 */
export function agruparLotes(linhasGerencial) {
  if (!Array.isArray(linhasGerencial)) throw new TypeError("agruparLotes espera a lista de linhas da GERENCIAL.");
  const grupos = new Map();
  linhasGerencial.forEach((linha) => {
    const numeroDoLote = texto(linha?.[F.NUMERO_DO_LOTE]);
    const cliente = texto(linha?.[F.NOME_DO_CLIENTE]);
    if (!numeroDoLote || !cliente) return;
    const chave = chaveDoLote(numeroDoLote, cliente);
    if (!grupos.has(chave)) grupos.set(chave, { numeroDoLote, ambientes: [] });
    grupos.get(chave).ambientes.push(paraAmbiente(linha));
  });
  return [...grupos.entries()]
    .map(([chave, { numeroDoLote, ambientes }]) => montarLote(chave, numeroDoLote, ambientes))
    .filter((lote) => lote.otimizacao)
    .sort((a, b) => (a.otimizacao === b.otimizacao
      ? b.numero.localeCompare(a.numero, "pt-BR", { numeric: true })
      : b.otimizacao.localeCompare(a.otimizacao)));
}

/** Datas distintas de um campo nos ambientes do lote, em ordem. */
export function datasDoCampo(lote, campo) {
  return [...new Set(lote.ambientes.map((a) => a.linha[campo]).filter(Boolean))].sort();
}

/**
 * Data do cartão do cabeçalho. Ambientes do mesmo lote costumam ter a mesma
 * data; quando não têm, vale a mais cedo (a que aperta primeiro) e o aviso
 * de datas divergentes conta o resto.
 */
export function dataDoLote(lote, campo) {
  return datasDoCampo(lote, campo)[0] || null;
}

export function entregaDoLote(lote) {
  return dataDoLote(lote, CAMPO_ENTREGA);
}
